import { useEffect, useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useData } from '../../context/DataContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { calculateMonthlyPayment, calculateRemainingTerm } from '../../utils/mortgageCalculations';
import { formatCurrency } from '../../utils/formatters';
import dayjs from 'dayjs';

const MortgageModal = ({ visible, onCancel, mortgage }) => {
  const { addMortgage, updateMortgage, properties } = useData();
  const [formData, setFormData] = useState({
    name: '',
    lender: '',
    linkedPropertyId: '',
    originalBalance: 0,
    currentBalance: 0,
    startDate: '',
    termYears: 25,
    fixedRate: 4.5,
    fixedTermEndDate: '',
    svrRate: 6.5,
    overpaymentMonthly: 0,
    repaymentType: 'repayment',
    currency: 'GBP',
    notes: '',
  });
  const [errors, setErrors] = useState({});

  useEscapeKey(onCancel, visible);

  useEffect(() => {
    if (visible) {
      if (mortgage) {
        setFormData({
          name: mortgage.name || '',
          lender: mortgage.lender || '',
          linkedPropertyId: mortgage.linkedPropertyId || '',
          originalBalance: mortgage.originalBalance || 0,
          currentBalance: mortgage.currentBalance || 0,
          startDate: mortgage.startDate || '',
          termYears: mortgage.termYears || 25,
          fixedRate: mortgage.fixedRate ?? 4.5,
          fixedTermEndDate: mortgage.fixedTermEndDate || '',
          svrRate: mortgage.svrRate ?? 6.5,
          overpaymentMonthly: mortgage.overpaymentMonthly || 0,
          repaymentType: mortgage.repaymentType || 'repayment',
          currency: mortgage.currency || 'GBP',
          notes: mortgage.notes || '',
        });
      } else {
        const defaultFixedEnd = dayjs().add(2, 'year').format('YYYY-MM-DD');
        setFormData({
          name: '',
          lender: '',
          linkedPropertyId: '',
          originalBalance: 0,
          currentBalance: 0,
          startDate: dayjs().format('YYYY-MM-DD'),
          termYears: 25,
          fixedRate: 4.5,
          fixedTermEndDate: defaultFixedEnd,
          svrRate: 6.5,
          overpaymentMonthly: 0,
          repaymentType: 'repayment',
          currency: 'GBP',
          notes: '',
        });
      }
      setErrors({});
    }
  }, [visible, mortgage]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Please enter a mortgage name';
    if (formData.currentBalance <= 0) newErrors.currentBalance = 'Please enter the current balance';
    if (formData.termYears <= 0) newErrors.termYears = 'Please enter the term';
    if (!formData.startDate) newErrors.startDate = 'Please enter the start date';
    if (formData.fixedRate < 0) newErrors.fixedRate = 'Rate cannot be negative';
    if (formData.svrRate < 0) newErrors.svrRate = 'Rate cannot be negative';
    if (!formData.fixedTermEndDate) newErrors.fixedTermEndDate = 'Please enter the fixed term end date';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const data = {
      ...formData,
      originalBalance: parseFloat(formData.originalBalance) || 0,
      currentBalance: parseFloat(formData.currentBalance) || 0,
      termYears: parseInt(formData.termYears) || 25,
      fixedRate: parseFloat(formData.fixedRate) || 0,
      svrRate: parseFloat(formData.svrRate) || 0,
      overpaymentMonthly: parseFloat(formData.overpaymentMonthly) || 0,
    };

    if (mortgage) {
      updateMortgage(mortgage.id, data);
    } else {
      addMortgage(data);
    }
    onCancel();
  };

  // Calculate estimated monthly payment for preview
  const remainingMonths = formData.startDate && formData.termYears
    ? Math.max(1, calculateRemainingTerm({ startDate: formData.startDate, termYears: formData.termYears }))
    : formData.termYears * 12;

  const now = dayjs();
  const isInFixed = formData.fixedTermEndDate && now.isBefore(dayjs(formData.fixedTermEndDate));
  const currentRate = isInFixed ? formData.fixedRate : formData.svrRate;
  const estimatedPayment = calculateMonthlyPayment(
    parseFloat(formData.currentBalance) || 0,
    currentRate,
    remainingMonths,
    formData.repaymentType
  );

  // Available properties that aren't already linked to another mortgage
  const availableProperties = properties.filter(p =>
    !p.linkedMortgageId || p.linkedMortgageId === mortgage?.id
  );

  return (
    <Transition appear show={visible} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-lg transform rounded-2xl bg-white p-6 text-left shadow-xl transition-all max-h-[90vh] overflow-y-auto">
                <Dialog.Title as="h3" className="text-base font-semibold text-gray-900 mb-4">
                  {mortgage ? 'Edit Mortgage' : 'Add Mortgage'}
                </Dialog.Title>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mortgage Name <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Main Mortgage"
                        className={`block w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Lender</label>
                      <input
                        type="text"
                        value={formData.lender}
                        onChange={(e) => setFormData({ ...formData, lender: e.target.value })}
                        placeholder="e.g., Nationwide"
                        className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Original Balance</label>
                      <input
                        type="number"
                        step="1000"
                        min="0"
                        value={formData.originalBalance}
                        onChange={(e) => setFormData({ ...formData, originalBalance: parseFloat(e.target.value) || 0 })}
                        className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Balance <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="number"
                        step="100"
                        min="0"
                        value={formData.currentBalance}
                        onChange={(e) => setFormData({ ...formData, currentBalance: parseFloat(e.target.value) || 0 })}
                        className={`block w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.currentBalance ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {errors.currentBalance && <p className="mt-1 text-xs text-red-600">{errors.currentBalance}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                        className={`block w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.startDate ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {errors.startDate && <p className="mt-1 text-xs text-red-600">{errors.startDate}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Term (Years) <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="40"
                        value={formData.termYears}
                        onChange={(e) => setFormData({ ...formData, termYears: parseInt(e.target.value) || 25 })}
                        className={`block w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.termYears ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {errors.termYears && <p className="mt-1 text-xs text-red-600">{errors.termYears}</p>}
                    </div>
                  </div>

                  {/* Rate Structure */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">Interest Rate Structure</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Fixed Rate (%) <span className="text-red-600">*</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.fixedRate}
                          onChange={(e) => setFormData({ ...formData, fixedRate: parseFloat(e.target.value) || 0 })}
                          className={`block w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.fixedRate ? 'border-red-500' : 'border-gray-300'}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Fixed Until <span className="text-red-600">*</span>
                        </label>
                        <input
                          type="date"
                          value={formData.fixedTermEndDate}
                          onChange={(e) => setFormData({ ...formData, fixedTermEndDate: e.target.value })}
                          className={`block w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.fixedTermEndDate ? 'border-red-500' : 'border-gray-300'}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          SVR Rate (%)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.svrRate}
                          onChange={(e) => setFormData({ ...formData, svrRate: parseFloat(e.target.value) || 0 })}
                          className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Repayment Type</label>
                      <select
                        value={formData.repaymentType}
                        onChange={(e) => setFormData({ ...formData, repaymentType: e.target.value })}
                        className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="repayment">Repayment</option>
                        <option value="interest-only">Interest Only</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Overpayment</label>
                      <input
                        type="number"
                        step="50"
                        min="0"
                        value={formData.overpaymentMonthly}
                        onChange={(e) => setFormData({ ...formData, overpaymentMonthly: parseFloat(e.target.value) || 0 })}
                        className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Linked Property</label>
                    <select
                      value={formData.linkedPropertyId}
                      onChange={(e) => setFormData({ ...formData, linkedPropertyId: e.target.value })}
                      className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">None</option>
                      {availableProperties.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Estimated Payment Preview */}
                  {formData.currentBalance > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs text-blue-800">
                        Estimated monthly payment at {currentRate}% ({isInFixed ? 'Fixed' : 'SVR'}): <strong>{formatCurrency(estimatedPayment, formData.currency)}</strong>
                        {formData.overpaymentMonthly > 0 && (
                          <span> + {formatCurrency(formData.overpaymentMonthly, formData.currency)} overpayment</span>
                        )}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={2}
                      className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Any additional notes..."
                    />
                  </div>

                  <div className="flex gap-3 justify-end pt-2">
                    <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                      style={{ backgroundColor: '#012E58' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
                    >
                      {mortgage ? 'Update Mortgage' : 'Add Mortgage'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MortgageModal;
